import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Swiper from "swiper";
import {
  Navigation,
  Autoplay,
  Pagination,
  EffectFade,
  Thumbs,
} from "swiper/modules";

// GSAPプラグイン登録
gsap.registerPlugin(ScrollTrigger);

// モバイル判定
const mobileQuery = window.matchMedia("(max-width: 768px)");
let isMobile = mobileQuery.matches;

mobileQuery.addListener((e) => {
  isMobile = e.matches;
});

// DOMContentLoaded
const init = function () {
  if (window.isBundleInitialized) return;
  window.isBundleInitialized = true;
  // ====================
  // スクロールアニメーション
  // ====================
  const scrollElements = document.querySelectorAll(".is-scroll-animation");
  const observerOptions = { threshold: 0.2 };
  const scrollObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-animate");
        observer.unobserve(entry.target);
      }
    });
  }, observerOptions);

  scrollElements.forEach((el) => scrollObserver.observe(el));

  // ====================
  // クリッピングアニメーション
  // ====================
  const clipAnimations = document.querySelectorAll(".animation-clip");
  clipAnimations.forEach((element) => {
    if (isMobile) {
      gsap.set(element, {
        opacity: 1,
        y: 0,
        clearProps: "clip-path",
      });
    } else {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: element,
          start: "top bottom",
          toggleActions: "play none none none",
        },
      });

      tl.from(element.children, {
        yPercent: 100,
        duration: 1,
        ease: "power4.out",
      }).fromTo(
        element,
        { clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)" },
        {
          clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
          duration: 1,
          ease: "power4.inOut",
        },
        "<0.2"
      );
    }
  });

  // ====================
  // メインビジュアルスライダー
  // ====================
  const mainVisualSwiper = document.querySelector(".mainVisualSwiper");
  if (mainVisualSwiper) {
    new Swiper(".mainVisualSwiper", {
      modules: [EffectFade, Autoplay],
      effect: "fade",
      fadeEffect: {
        crossFade: true,
      },
      loop: true,
      speed: 1500,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false,
      },
      allowTouchMove: false, // スワイプ無効（自動再生のみ）
    });
  }

  // ====================
  // Swiper基本設定
  // ====================
  const swiperPrimary = document.querySelector(".swiper-primary");
  if (swiperPrimary) {
    new Swiper(".swiper-primary", {
      modules: [Navigation, Autoplay],
      slidesPerView: "auto",
      centeredSlides: false,
      spaceBetween: 30,
      loop: false,
      speed: 500,
      navigation: {
        nextEl: ".swiper-primary-button-next",
        prevEl: ".swiper-primary-button-prev",
      },
      autoplay: false,
      allowTouchMove: true,
      breakpoints: {
        320: { slidesPerView: "auto", spaceBetween: 20 },
        768: { slidesPerView: "auto", spaceBetween: 30 },
        1024: { slidesPerView: "auto", spaceBetween: 30 },
      },
    });
  }

  // ====================
  // 無限スクロールスライダー
  // ====================
  const infiniteScrollSwiper = document.querySelector(
    ".infinite-scroll-swiper"
  );
  if (infiniteScrollSwiper) {
    new Swiper(".infinite-scroll-swiper", {
      modules: [Autoplay],
      slidesPerView: "auto",
      spaceBetween: 0,
      loop: true,
      speed: 10000,
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
        reverseDirection: true,
      },
      freeMode: true,
      freeModeMomentum: true,
      allowTouchMove: true,
    });
  }

  // ====================
  // FAQアコーディオン
  // ====================
  const accordionItems = document.querySelectorAll(".accordion-item");

  accordionItems.forEach((item) => {
    const checkbox = item.querySelector('input[type="checkbox"]');
    const contentDiv = item.querySelector(".max-h-0");
    const plusIcon = item.querySelector(".accordion-icon.plus");
    const minusIcon = item.querySelector(".accordion-icon.minus");

    if (checkbox && contentDiv && plusIcon && minusIcon) {
      checkbox.addEventListener("change", () => {
        if (checkbox.checked) {
          contentDiv.style.maxHeight = `${contentDiv.scrollHeight}px`;
          plusIcon.style.opacity = "0";
          minusIcon.style.opacity = "1";
        } else {
          contentDiv.style.maxHeight = "0";
          plusIcon.style.opacity = "1";
          minusIcon.style.opacity = "0";
        }
      });
    }
  });

  // ====================
  // タブ機能
  // ====================
  const tabButtons = document.querySelectorAll(".works-tab-btn");
  const allCategoryGroups = document.querySelectorAll(".category-group");

  if (tabButtons.length > 0 && allCategoryGroups.length > 0) {
    // 初期状態
    showCardsForCategory("all");

    const allButton = document.querySelector(
      '.works-tab-btn[data-category="all"]'
    );
    if (allButton) {
      allButton.classList.add("active");
      allButton.classList.remove("bg-background-tertiary", "text-black");
      allButton.classList.add("bg-background-secondary", "text-white");
    }

    // タブクリック時
    tabButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const selectedCategory = this.dataset.category;

        tabButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.remove("bg-background-secondary", "text-white");
          btn.classList.add("bg-background-tertiary", "text-black");
        });

        this.classList.add("active");
        this.classList.remove("bg-background-tertiary", "text-black");
        this.classList.add("bg-background-secondary", "text-white");

        showCardsForCategory(selectedCategory);
      });
    });

    function showCardsForCategory(category) {
      allCategoryGroups.forEach((group) => {
        if (group.dataset.category === category) {
          group.classList.remove("hidden");
          group.style.display = "contents";
        } else {
          group.classList.add("hidden");
          group.style.display = "none";
        }
      });
    }
  }

  // ====================
  // ドロップダウンメニュー（デスクトップ）
  // ====================
  const toggle = document.getElementById("dropdown-toggle");
  const dropdown = document.getElementById("dropdown-menu");
  if (toggle && dropdown) {
    toggle.addEventListener("click", (e) => {
      e.preventDefault();
      if (dropdown.classList.contains("hidden")) {
        dropdown.classList.remove("hidden");
        gsap.fromTo(
          dropdown,
          { opacity: 0, y: -10 },
          { opacity: 1, y: 0, duration: 0.3 }
        );
      } else {
        gsap.to(dropdown, {
          opacity: 0,
          y: -10,
          duration: 0.3,
          onComplete: () => dropdown.classList.add("hidden"),
        });
      }
    });
  }

  // ====================
  // モバイルハンバーガーメニュー
  // ====================
  const mobileToggle = document.getElementById("mobile-toggle");
  const mobileMenu = document.getElementById("mobile-menu");
  const floatingCta = document.getElementById("floating-cta");
  let mobileMenuJustToggled = false;

  if (mobileToggle && mobileMenu) {
    console.log("[DEBUG] Mobile menu elements found");
    mobileToggle.addEventListener("click", (e) => {
      console.log("[DEBUG] Mobile toggle clicked");
      e.preventDefault();
      e.stopPropagation();

      mobileMenuJustToggled = true;
      setTimeout(() => {
        mobileMenuJustToggled = false;
      }, 100);

      if (mobileMenu.classList.contains("hidden")) {
        console.log("[DEBUG] Opening mobile menu");
        // メニューを開く（フェードイン）
        mobileMenu.classList.remove("hidden");
        gsap.fromTo(
          mobileMenu,
          { opacity: 0 },
          { opacity: 1, duration: 0.3, ease: "power2.out" }
        );
        mobileToggle.classList.add("active");
        // floating-ctaを非表示（モバイルのみ）
        if (floatingCta && window.innerWidth < 1024) {
          floatingCta.style.display = "none";
        }
      } else {
        // メニューを閉じる（フェードアウト）
        gsap.to(mobileMenu, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => {
            mobileMenu.classList.add("hidden");
            mobileToggle.classList.remove("active");
            // floating-ctaを表示（モバイルのみ）
            if (floatingCta && window.innerWidth < 1024) {
              floatingCta.style.display = "";
            }
          },
        });
      }
    });
  }

  // ====================
  // モバイルメニュー内のアコーディオン
  // ====================
  const accordionTriggers = document.querySelectorAll(".accordion-trigger");

  accordionTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      e.preventDefault();

      const isExpanded = trigger.getAttribute("aria-expanded") === "true";
      const targetId = trigger.getAttribute("aria-controls");
      const targetContent = document.getElementById(targetId);
      const plusIcon = trigger.querySelector(".accordion-icon-plus");
      const minusIcon = trigger.querySelector(".accordion-icon-minus");

      if (!targetContent) return;

      if (isExpanded) {
        // 閉じる
        trigger.setAttribute("aria-expanded", "false");

        // アイコン切り替え
        if (plusIcon && minusIcon) {
          plusIcon.classList.remove("hidden");
          minusIcon.classList.add("hidden");
        }

        // GSAPでアニメーション
        gsap.to(targetContent, {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
          onComplete: () => {
            targetContent.classList.add("hidden");
          },
        });
      } else {
        // 開く
        trigger.setAttribute("aria-expanded", "true");

        // アイコン切り替え
        if (plusIcon && minusIcon) {
          plusIcon.classList.add("hidden");
          minusIcon.classList.remove("hidden");
        }

        // 高さを測定するために一時的に表示
        targetContent.classList.remove("hidden");
        const height = targetContent.scrollHeight;

        // GSAPでアニメーション
        gsap.fromTo(
          targetContent,
          { height: 0, opacity: 0 },
          {
            height: height,
            opacity: 1,
            duration: 0.3,
            ease: "power2.out",
            onComplete: () => {
              // アニメーション完了後、heightをautoに設定（レスポンシブ対応）
              targetContent.style.height = "auto";
            },
          }
        );
      }
    });
  });

  // ====================
  // フッターアコーディオン（モバイルのみ）
  // ====================
  const footerAccordionTriggers = document.querySelectorAll(
    ".footer-accordion-trigger"
  );

  footerAccordionTriggers.forEach((trigger) => {
    trigger.addEventListener("click", (e) => {
      // デスクトップ（1160px以上）では動作させない
      if (window.innerWidth >= 1160) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const isExpanded = trigger.getAttribute("aria-expanded") === "true";
      const targetId = trigger.getAttribute("aria-controls");
      const targetContent = document.getElementById(targetId);
      const plusIcon = trigger.querySelector(".footer-accordion-icon-plus");
      const minusIcon = trigger.querySelector(".footer-accordion-icon-minus");

      if (!targetContent) return;

      if (isExpanded) {
        // 閉じる
        trigger.setAttribute("aria-expanded", "false");

        // アイコン切り替え
        if (plusIcon && minusIcon) {
          plusIcon.classList.remove("hidden");
          minusIcon.classList.add("hidden");
        }

        // GSAPでアニメーション
        gsap.to(targetContent, {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: "power2.out",
          onComplete: () => {
            targetContent.classList.add("hidden");
          },
        });
      } else {
        // 開く
        trigger.setAttribute("aria-expanded", "true");

        // アイコン切り替え
        if (plusIcon && minusIcon) {
          plusIcon.classList.add("hidden");
          minusIcon.classList.remove("hidden");
        }

        // 高さを測定するために一時的に表示
        targetContent.classList.remove("hidden");
        const height = targetContent.scrollHeight;

        // GSAPでアニメーション
        gsap.fromTo(
          targetContent,
          { height: 0, opacity: 0 },
          {
            height: height,
            opacity: 1,
            duration: 0.3,
            ease: "power2.out",
            onComplete: () => {
              // アニメーション完了後、heightをautoに設定（レスポンシブ対応）
              targetContent.style.height = "auto";
            },
          }
        );
      }
    });
  });

  // ====================
  // メニュー外クリックで閉じる
  // ====================
  document.addEventListener("click", (e) => {
    // デスクトップドロップダウンを閉じる
    if (
      dropdown &&
      toggle &&
      !dropdown.contains(e.target) &&
      !toggle.contains(e.target)
    ) {
      if (!dropdown.classList.contains("hidden")) {
        gsap.to(dropdown, {
          opacity: 0,
          y: -10,
          duration: 0.3,
          onComplete: () => dropdown.classList.add("hidden"),
        });
      }
    }

    // モバイルメニューを閉じる
    if (
      mobileMenu &&
      mobileToggle &&
      !mobileMenuJustToggled &&
      !mobileMenu.contains(e.target) &&
      !mobileToggle.contains(e.target)
    ) {
      console.log("[DEBUG] Document click - closing mobile menu");
      if (!mobileMenu.classList.contains("hidden")) {
        gsap.to(mobileMenu, {
          opacity: 0,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => {
            mobileMenu.classList.add("hidden");
            mobileToggle.classList.remove("active");
          },
        });
      }
    }
  });

  // ====================
  // イベント・見学会セクション: タブ切り替え + Swiper
  // ====================
  const eventTabButtons = document.querySelectorAll(".event-tab-button");
  const eventTabContents = document.querySelectorAll(".event-tab-content");

  // Swiper初期化（3つのタブ用）
  let eventSwipers = {};

  // 新築用Swiper
  const swiperNewConstruction = document.querySelector(
    ".event-swiper-new-construction"
  );
  if (swiperNewConstruction) {
    eventSwipers["new-construction"] = new Swiper(
      ".event-swiper-new-construction",
      {
        modules: [Navigation],
        slidesPerView: "auto",
        spaceBetween: 20,
        loop: false,
        navigation: {
          nextEl: ".event-swiper-button-next-new-construction",
          prevEl: ".event-swiper-button-prev-new-construction",
        },
        breakpoints: {
          1160: {
            spaceBetween: 30,
          },
        },
      }
    );
  }

  // リフォーム・リノベーション用Swiper
  const swiperRenovation = document.querySelector(".event-swiper-renovation");
  if (swiperRenovation) {
    eventSwipers["renovation"] = new Swiper(".event-swiper-renovation", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".event-swiper-button-next-renovation",
        prevEl: ".event-swiper-button-prev-renovation",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // イベント用Swiper
  const swiperEvents = document.querySelector(".event-swiper-events");
  if (swiperEvents) {
    eventSwipers["events"] = new Swiper(".event-swiper-events", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".event-swiper-button-next-events",
        prevEl: ".event-swiper-button-prev-events",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // タブ切り替え機能
  if (eventTabButtons.length > 0 && eventTabContents.length > 0) {
    // 初期状態: 新築タブのコンテンツを表示
    const initialTab = "new-construction";
    eventTabContents.forEach((content) => {
      const contentTab = content.getAttribute("data-tab-content");
      if (contentTab === initialTab) {
        content.classList.remove("hidden");
        content.classList.add("active");
        // Swiperを更新
        if (eventSwipers[initialTab]) {
          eventSwipers[initialTab].update();
        }
      } else {
        content.classList.add("hidden");
        content.classList.remove("active");
      }
    });

    // タブクリックイベント
    eventTabButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const targetTab = this.getAttribute("data-tab");

        // すべてのタブボタンから active クラスを削除
        eventTabButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.remove("border-primary");
          btn.classList.add("border-accent-100");
        });

        // クリックされたタブに active クラスを追加
        this.classList.add("active");
        this.classList.remove("border-accent-100");
        this.classList.add("border-primary");

        // すべてのコンテンツを非表示にし、対象のコンテンツのみ表示
        eventTabContents.forEach((content) => {
          const contentTab = content.getAttribute("data-tab-content");
          if (contentTab === targetTab) {
            content.classList.remove("hidden");
            content.classList.add("active");
            // Swiperを再初期化（表示状態になったときに適切にレイアウトを再計算）
            if (eventSwipers[targetTab]) {
              eventSwipers[targetTab].update();
            }
          } else {
            content.classList.add("hidden");
            content.classList.remove("active");
          }
        });

        // ナビゲーションボタンの表示切り替え
        const navButtons = document.querySelectorAll(".event-nav-button");
        navButtons.forEach((btn) => {
          const btnTarget = btn.getAttribute("data-target");
          if (btnTarget === targetTab) {
            btn.classList.remove("hidden");
          } else {
            btn.classList.add("hidden");
          }
        });
      });
    });
  }

  // ====================
  // 土地・分譲地情報セクション: Swiper
  // ====================
  const landSwiper = document.querySelector(".land-swiper");
  if (landSwiper) {
    new Swiper(".land-swiper", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".land-swiper-button-next",
        prevEl: ".land-swiper-button-prev",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // ====================
  // 施工事例セクション: タブ切り替え + Swiper
  // ====================
  const worksTabButtons = document.querySelectorAll(".works-tab-button");
  const worksTabContents = document.querySelectorAll(".works-tab-content");

  // Swiper初期化（3つのタブ用）
  let worksSwipers = {};

  // 新築用Swiper
  const swiperWorksNewConstruction = document.querySelector(
    ".works-swiper-new-construction"
  );
  if (swiperWorksNewConstruction) {
    worksSwipers["new-construction"] = new Swiper(
      ".works-swiper-new-construction",
      {
        modules: [Navigation],
        slidesPerView: "auto",
        spaceBetween: 20,
        loop: false,
        navigation: {
          nextEl: ".works-swiper-button-next-new-construction",
          prevEl: ".works-swiper-button-prev-new-construction",
        },
        breakpoints: {
          1160: {
            spaceBetween: 30,
          },
        },
      }
    );
  }

  // リノベーション用Swiper
  const swiperWorksRenovation = document.querySelector(
    ".works-swiper-renovation"
  );
  if (swiperWorksRenovation) {
    worksSwipers["renovation"] = new Swiper(".works-swiper-renovation", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".works-swiper-button-next-renovation",
        prevEl: ".works-swiper-button-prev-renovation",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // リフォーム用Swiper
  const swiperWorksReform = document.querySelector(".works-swiper-reform");
  if (swiperWorksReform) {
    worksSwipers["reform"] = new Swiper(".works-swiper-reform", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".works-swiper-button-next-reform",
        prevEl: ".works-swiper-button-prev-reform",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // タブ切り替え機能
  if (worksTabButtons.length > 0 && worksTabContents.length > 0) {
    // 初期状態: 新築タブのコンテンツを表示
    const initialWorksTab = "new-construction";
    worksTabContents.forEach((content) => {
      const contentTab = content.getAttribute("data-tab-content");
      if (contentTab === initialWorksTab) {
        content.classList.remove("hidden");
        content.classList.add("active");
        // Swiperを更新
        if (worksSwipers[initialWorksTab]) {
          worksSwipers[initialWorksTab].update();
        }
      } else {
        content.classList.add("hidden");
        content.classList.remove("active");
      }
    });

    // タブクリックイベント
    worksTabButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const targetTab = this.getAttribute("data-tab");

        // すべてのタブボタンから active クラスを削除
        worksTabButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.remove("border-primary");
          btn.classList.add("border-accent-100");
        });

        // クリックされたタブに active クラスを追加
        this.classList.add("active");
        this.classList.remove("border-accent-100");
        this.classList.add("border-primary");

        // すべてのコンテンツを非表示にし、対象のコンテンツのみ表示
        worksTabContents.forEach((content) => {
          const contentTab = content.getAttribute("data-tab-content");
          if (contentTab === targetTab) {
            content.classList.remove("hidden");
            content.classList.add("active");
            // Swiperを再初期化（表示状態になったときに適切にレイアウトを再計算）
            if (worksSwipers[targetTab]) {
              worksSwipers[targetTab].update();
            }
          } else {
            content.classList.add("hidden");
            content.classList.remove("active");
          }
        });

        // ナビゲーションボタンの表示切り替え
        const worksNavButtons = document.querySelectorAll(".works-nav-button");
        worksNavButtons.forEach((btn) => {
          const btnTarget = btn.getAttribute("data-target");
          if (btnTarget === targetTab) {
            btn.classList.remove("hidden");
          } else {
            btn.classList.add("hidden");
          }
        });
      });
    });
  }

  // ====================
  // お客様の声セクション: タブ切り替え + Swiper
  // ====================
  const voiceTabButtons = document.querySelectorAll(".voice-tab-button");
  const voiceTabContents = document.querySelectorAll(".voice-tab-content");

  // Swiper初期化（3つのタブ用）
  let voiceSwipers = {};

  // 新築用Swiper
  const voiceSwiperNewConstruction = document.querySelector(
    ".voice-swiper-new-construction"
  );
  if (voiceSwiperNewConstruction) {
    voiceSwipers["new-construction"] = new Swiper(
      ".voice-swiper-new-construction",
      {
        modules: [Navigation],
        slidesPerView: "auto",
        spaceBetween: 20,
        loop: false,
        navigation: {
          nextEl: ".voice-swiper-button-next-new-construction",
          prevEl: ".voice-swiper-button-prev-new-construction",
        },
        breakpoints: {
          1160: {
            spaceBetween: 30,
          },
        },
      }
    );
  }

  // リノベーション用Swiper
  const voiceSwiperRenovation = document.querySelector(
    ".voice-swiper-renovation"
  );
  if (voiceSwiperRenovation) {
    voiceSwipers["renovation"] = new Swiper(".voice-swiper-renovation", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".voice-swiper-button-next-renovation",
        prevEl: ".voice-swiper-button-prev-renovation",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // リフォーム用Swiper
  const voiceSwiperReform = document.querySelector(".voice-swiper-reform");
  if (voiceSwiperReform) {
    voiceSwipers["reform"] = new Swiper(".voice-swiper-reform", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".voice-swiper-button-next-reform",
        prevEl: ".voice-swiper-button-prev-reform",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // タブ切り替え機能
  if (voiceTabButtons.length > 0 && voiceTabContents.length > 0) {
    // 初期状態: 新築タブのコンテンツを表示
    const initialTab = "new-construction";
    voiceTabContents.forEach((content) => {
      const contentTab = content.getAttribute("data-tab-content");
      if (contentTab === initialTab) {
        content.classList.remove("hidden");
        content.classList.add("active");
        // Swiperを更新
        if (voiceSwipers[initialTab]) {
          voiceSwipers[initialTab].update();
        }
      } else {
        content.classList.add("hidden");
        content.classList.remove("active");
      }
    });

    // タブクリックイベント
    voiceTabButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const targetTab = this.getAttribute("data-tab");

        // すべてのタブボタンから active クラスを削除
        voiceTabButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.remove("border-primary");
          btn.classList.add("border-accent-100");
        });

        // クリックされたタブに active クラスを追加
        this.classList.add("active");
        this.classList.remove("border-accent-100");
        this.classList.add("border-primary");

        // すべてのコンテンツを非表示にし、対象のコンテンツのみ表示
        voiceTabContents.forEach((content) => {
          const contentTab = content.getAttribute("data-tab-content");
          if (contentTab === targetTab) {
            content.classList.remove("hidden");
            content.classList.add("active");
            // Swiperを再初期化（表示状態になったときに適切にレイアウトを再計算）
            if (voiceSwipers[targetTab]) {
              voiceSwipers[targetTab].update();
            }
          } else {
            content.classList.add("hidden");
            content.classList.remove("active");
          }
        });

        // ナビゲーションボタンの表示切り替え
        const voiceNavButtons = document.querySelectorAll(".voice-nav-button");
        voiceNavButtons.forEach((btn) => {
          const btnTarget = btn.getAttribute("data-target");
          if (btnTarget === targetTab) {
            btn.classList.remove("hidden");
          } else {
            btn.classList.add("hidden");
          }
        });
      });
    });
  }

  // ====================
  // 住宅展示場・モデルハウスセクション: Swiper
  // ====================
  const modelhouseSwiper = document.querySelector(".modelhouse-swiper");
  if (modelhouseSwiper) {
    new Swiper(".modelhouse-swiper", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".modelhouse-swiper-button-next",
        prevEl: ".modelhouse-swiper-button-prev",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // ====================
  // 健康住宅研究所コラムセクション: タブ切り替え + Swiper
  // ====================
  const columnTabButtons = document.querySelectorAll(".column-tab-button");
  const columnTabContents = document.querySelectorAll(".column-tab-content");
  let columnSwipers = {};

  // 各タブのSwiperを初期化
  const columnSwiperColumn = document.querySelector(".column-swiper-column");
  if (columnSwiperColumn) {
    columnSwipers["column"] = new Swiper(".column-swiper-column", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".column-swiper-button-next-column",
        prevEl: ".column-swiper-button-prev-column",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  const columnSwiperReport = document.querySelector(".column-swiper-report");
  if (columnSwiperReport) {
    columnSwipers["report"] = new Swiper(".column-swiper-report", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".column-swiper-button-next-report",
        prevEl: ".column-swiper-button-prev-report",
      },
      breakpoints: {
        1160: {
          spaceBetween: 30,
        },
      },
    });
  }

  // タブ切り替え機能
  if (columnTabButtons.length > 0 && columnTabContents.length > 0) {
    // 初期状態: コラムタブのコンテンツを表示
    const initialTab = "column";
    columnTabContents.forEach((content) => {
      const contentTab = content.getAttribute("data-tab-content");
      if (contentTab === initialTab) {
        content.classList.remove("hidden");
        content.classList.add("active");
        // Swiperを更新
        if (columnSwipers[initialTab]) {
          columnSwipers[initialTab].update();
        }
      } else {
        content.classList.add("hidden");
        content.classList.remove("active");
      }
    });

    // タブクリックイベント
    columnTabButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const targetTab = this.getAttribute("data-tab");

        // すべてのタブボタンから active クラスを削除
        columnTabButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.remove("border-primary");
          btn.classList.add("border-accent-100");
        });

        // クリックされたタブに active クラスを追加
        this.classList.add("active");
        this.classList.remove("border-accent-100");
        this.classList.add("border-primary");

        // すべてのコンテンツを非表示にし、対象のコンテンツのみ表示
        columnTabContents.forEach((content) => {
          const contentTab = content.getAttribute("data-tab-content");
          if (contentTab === targetTab) {
            content.classList.remove("hidden");
            content.classList.add("active");
            // Swiperを再初期化（表示状態になったときに適切にレイアウトを再計算）
            if (columnSwipers[targetTab]) {
              columnSwipers[targetTab].update();
            }
          } else {
            content.classList.add("hidden");
            content.classList.remove("active");
          }
        });

        // ナビゲーションボタンの表示切り替え
        const columnNavButtons =
          document.querySelectorAll(".column-nav-button");
        columnNavButtons.forEach((btn) => {
          const btnTarget = btn.getAttribute("data-target");
          if (btnTarget === targetTab) {
            btn.classList.remove("hidden");
          } else {
            btn.classList.add("hidden");
          }
        });
      });
    });
  }

  // ====================
  // モバイル検索機能
  // ====================
  const mobileSearchToggle = document.getElementById("mobile-search-toggle");
  const mobileSearchOverlay = document.getElementById("mobile-search-overlay");
  const mobileSearchBox = document.getElementById("mobile-search-box");

  if (mobileSearchToggle && mobileSearchOverlay && mobileSearchBox) {
    // 検索窓を開く
    function openMobileSearch() {
      mobileSearchOverlay.classList.remove("hidden");
      mobileSearchBox.classList.remove("hidden");
      // フォーカスを検索窓に
      const searchInput = mobileSearchBox.querySelector("input");
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
    }

    // 検索窓を閉じる
    function closeMobileSearch() {
      mobileSearchOverlay.classList.add("hidden");
      mobileSearchBox.classList.add("hidden");
    }

    // 検索アイコンクリック
    mobileSearchToggle.addEventListener("click", function () {
      if (mobileSearchBox.classList.contains("hidden")) {
        openMobileSearch();
      } else {
        closeMobileSearch();
      }
    });

    // オーバーレイクリックで閉じる
    mobileSearchOverlay.addEventListener("click", closeMobileSearch);

    // ESCキーで閉じる
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && !mobileSearchBox.classList.contains("hidden")) {
        closeMobileSearch();
      }
    });
  }

  // ====================
  // ヘッダーのスクロール時の挙動
  // ====================
  let lastScroll = 0;
  const header = document.querySelector(".header");

  if (header) {
    window.addEventListener("scroll", () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > 100) {
        header.classList.add("scrolled");
      } else {
        header.classList.remove("scrolled");
      }

      lastScroll = currentScroll;
    });
  }

  // ====================
  // YARPP関連記事スライダー
  // ====================
  const swiperYarpp = document.querySelector(".yarpp-swiper");
  if (swiperYarpp) {
    new Swiper(".yarpp-swiper", {
      modules: [Navigation],
      slidesPerView: "auto",
      spaceBetween: 20,
      loop: false,
      navigation: {
        nextEl: ".yarpp-swiper-button-next",
        prevEl: ".yarpp-swiper-button-prev",
      },
      breakpoints: {
        1160: {
          spaceBetween: 32,
        },
      },
    });
  }

  // ====================
  // Estate Gallery Swiper
  // ====================
  const estateMainSwiper = document.querySelector(".estate-main-swiper");
  const estateThumbnailSwiper = document.querySelector(
    ".estate-thumbnail-swiper"
  );

  if (estateMainSwiper && estateThumbnailSwiper) {
    // サムネイルスライドが存在するか確認
    const thumbnailSlides =
      estateThumbnailSwiper.querySelectorAll(".swiper-slide");
    const hasThumbnails = thumbnailSlides.length > 0;

    // キャプション更新関数
    const updateCaption = (swiper) => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      const caption = activeSlide?.getAttribute("data-caption") || "";
      const captionElement = document.querySelector(".estate-gallery-caption");
      if (captionElement) {
        captionElement.textContent = caption;
      }
    };

    // Thumbnail swiper - スライドが存在する場合のみ初期化
    let thumbnailSwiper = null;
    if (hasThumbnails) {
      thumbnailSwiper = new Swiper(".estate-thumbnail-swiper", {
        spaceBetween: 4,
        slidesPerView: "auto",
        watchSlidesProgress: true,
      });
    }

    // Main swiper options
    const mainSwiperOptions = {
      modules: [Navigation, Thumbs],
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: false,
      navigation: {
        nextEl: ".estate-swiper-button-next",
        prevEl: ".estate-swiper-button-prev",
      },
      on: {
        init: function () {
          updateCaption(this);
        },
        slideChange: function () {
          updateCaption(this);
        },
      },
    };

    // サムネイルが存在する場合のみthumbsオプションを追加
    if (hasThumbnails && thumbnailSwiper) {
      mainSwiperOptions.thumbs = {
        swiper: thumbnailSwiper,
      };
    }

    const mainSwiper = new Swiper(".estate-main-swiper", mainSwiperOptions);
  }

  // ====================
  // Modelhouse Gallery Swiper
  // ====================
  const modelhouseMainSwiper = document.querySelector(
    ".modelhouse-main-swiper"
  );
  const modelhouseThumbnailSwiper = document.querySelector(
    ".modelhouse-thumbnail-swiper"
  );

  if (modelhouseMainSwiper && modelhouseThumbnailSwiper) {
    // サムネイルスライドが存在するか確認
    const thumbnailSlides =
      modelhouseThumbnailSwiper.querySelectorAll(".swiper-slide");
    const hasThumbnails = thumbnailSlides.length > 0;

    // キャプション更新関数
    const updateModelhouseCaption = (swiper) => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      const caption = activeSlide?.getAttribute("data-caption") || "";
      const captionElement = document.querySelector(
        ".modelhouse-gallery-caption"
      );
      if (captionElement) {
        captionElement.textContent = caption;
      }
    };

    // Thumbnail swiper (サムネイルスライダー) - スライドが存在する場合のみ初期化
    let modelhouseThumbnail = null;
    if (hasThumbnails) {
      modelhouseThumbnail = new Swiper(".modelhouse-thumbnail-swiper", {
        spaceBetween: 4,
        slidesPerView: "auto",
        watchSlidesProgress: true,
      });
    }

    // Main swiper (メインスライダー)
    const mainSwiperOptions = {
      modules: [Navigation, Thumbs],
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: false,
      navigation: {
        nextEl: ".modelhouse-detail-swiper-button-next",
        prevEl: ".modelhouse-detail-swiper-button-prev",
      },
      on: {
        init: function () {
          // 初期表示時にキャプションを設定
          updateModelhouseCaption(this);
        },
        slideChange: function () {
          // スライド変更時にキャプションを更新
          updateModelhouseCaption(this);
        },
      },
    };

    // サムネイルが存在する場合のみthumbsオプションを追加
    if (hasThumbnails && modelhouseThumbnail) {
      mainSwiperOptions.thumbs = {
        swiper: modelhouseThumbnail,
      };
    }

    const modelhouseMain = new Swiper(
      ".modelhouse-main-swiper",
      mainSwiperOptions
    );
  }

  // ====================
  // Related Posts Swiper (施工事例詳細ページ)
  // ====================
  const relatedPostsSwiper = document.querySelector(".related-posts-swiper");
  if (relatedPostsSwiper) {
    new Swiper(".related-posts-swiper", {
      modules: [Navigation],
      slidesPerView: 1.2,
      spaceBetween: 16,
      breakpoints: {
        640: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 24,
        },
      },
      navigation: {
        nextEl: ".related-posts-next",
        prevEl: ".related-posts-prev",
      },
    });
  }

  // ====================
  // 投稿内のテーブルの横スクロール対応
  // ====================
  const postTables = document.querySelectorAll(".post-contents table");
  postTables.forEach((table) => {
    if (!table.parentElement.classList.contains("js-scrollable")) {
      const wrapper = document.createElement("div");
      wrapper.classList.add("js-scrollable", "overflow-x-auto");
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  });

  // ====================
  // Scroll Hint (横スクロールテーブルのヒント表示)
  // ====================
  const scrollableElements = document.querySelectorAll(".js-scrollable");
  if (scrollableElements.length > 0 && typeof ScrollHint !== "undefined") {
    new ScrollHint(".js-scrollable", {
      scrollHintIconAppendClass: "scroll-hint-icon-white",
      i18n: {
        scrollable: "スクロールできます",
      },
    });
  }

  // ====================
  // スタッフページ: アコーディオン
  // ====================
  const staffAccordionHeaders = document.querySelectorAll(
    ".staff-accordion-header"
  );

  staffAccordionHeaders.forEach((header) => {
    header.addEventListener("click", function () {
      const content = this.nextElementSibling;
      const isExpanded = this.getAttribute("aria-expanded") === "true";

      if (isExpanded) {
        // 閉じる
        content.style.maxHeight = "0";
        this.setAttribute("aria-expanded", "false");
      } else {
        // 開く
        content.style.maxHeight = content.scrollHeight + "px";
        this.setAttribute("aria-expanded", "true");
      }
    });
  });

  // ====================
  // Estate Archive: Load More
  // ====================
  const loadMoreButton = document.getElementById("load-more-estate");
  if (loadMoreButton) {
    const articleGrid = document.querySelector(
      ".grid.grid-cols-1.md\\:grid-cols-2.lg\\:grid-cols-3"
    );

    loadMoreButton.addEventListener("click", function () {
      const currentPage = parseInt(this.getAttribute("data-page"));
      const maxPages = parseInt(this.getAttribute("data-max-pages"));
      const nextPage = currentPage + 1;

      // ボタンを無効化
      this.disabled = true;
      this.style.opacity = "0.5";

      // AJAX リクエスト
      fetch(window.location.origin + "/wp/wp-admin/admin-ajax.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          action: "load_more_estate",
          page: nextPage,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // 新しい記事を追加
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = data.data.html;

            while (tempDiv.firstChild) {
              articleGrid.appendChild(tempDiv.firstChild);
            }

            // ページ番号を更新
            this.setAttribute("data-page", nextPage);
            this.disabled = false;
            this.style.opacity = "1";

            // 最後のページに到達したらボタンを非表示
            if (nextPage >= maxPages) {
              this.style.display = "none";
            }
          } else {
            console.error("Load more failed:", data.data.message);
            this.disabled = false;
            this.style.opacity = "1";
          }
        })
        .catch((error) => {
          console.error("AJAX error:", error);
          this.disabled = false;
          this.style.opacity = "1";
        });
    });
  }

  // ====================
  // スタッフページ: サイドメニューactive効果
  // ====================
  const departmentMenuItems = document.querySelectorAll(
    ".department-menu-item"
  );

  if (departmentMenuItems.length > 0) {
    const isDesktop = () => window.innerWidth >= 1024;
    const getOffset = () => 120; // CSSのscroll-mt-30（120px）と一致させる

    // メニューアイテムからセクションIDを取得
    const sectionIds = Array.from(departmentMenuItems)
      .map((item) => item.getAttribute("data-target"))
      .filter((id) => id);

    const departmentSections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((section) => section);

    if (departmentSections.length > 0) {
      // 初期状態: 最初のメニューをアクティブに
      departmentMenuItems[0].classList.add("is-active");

      // スムーズスクロール
      departmentMenuItems.forEach((item) => {
        item.addEventListener("click", function (e) {
          e.preventDefault();
          const targetId = this.getAttribute("data-target");
          const targetSection = document.getElementById(targetId);

          if (targetSection) {
            const offsetTop = targetSection.offsetTop - getOffset();
            window.scrollTo({
              top: offsetTop,
              behavior: "smooth",
            });
          }
        });
      });

      // スクロール位置に応じてactive切り替え
      let sectionObserver;

      const initObserver = () => {
        if (sectionObserver) sectionObserver.disconnect();

        // 判定位置をデバイスごとのオフセットに合わせる
        const offset = getOffset();
        // 指定のピクセル位置に来た瞬間に判定するため、rootMarginの上部を調整
        // -offsetpx 0px -90% 0px で、画面上部から offset px の位置を通過した瞬間を狙う
        const observerOptions = {
          root: null,
          rootMargin: `-${offset}px 0px -${
            window.innerHeight - offset - 1
          }px 0px`,
          threshold: 0,
        };

        sectionObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const sectionId = entry.target.getAttribute("id");

              departmentMenuItems.forEach((item) => {
                item.classList.remove("is-active");
                if (item.getAttribute("data-target") === sectionId) {
                  item.classList.add("is-active");
                }
              });
            }
          });
        }, observerOptions);

        departmentSections.forEach((section) => {
          sectionObserver.observe(section);
        });
      };

      initObserver();

      // リサイズ時にオフセットを再計算
      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(initObserver, 200);
      });
    }
  }

  // ====================
  // Archive: タブ切り替え（汎用）
  // ====================
  const archiveTabButtons = document.querySelectorAll(".archive-tab-button");
  const archiveContents = document.querySelectorAll(".archive-content");
  const archiveTags = document.querySelectorAll(".archive-tags, .gallery-tag-tabs");

  if (archiveTabButtons.length > 0 && archiveContents.length > 0) {
    archiveTabButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const targetCategory = this.getAttribute("data-category");

        // すべてのタブボタンから active クラスを削除
        archiveTabButtons.forEach((btn) => {
          btn.classList.remove("active");
          btn.classList.remove("border-primary");
          btn.classList.add("border-accent-100");
        });

        // クリックされたタブに active クラスを追加
        this.classList.add("active");
        this.classList.remove("border-accent-100");
        this.classList.add("border-primary");

        // すべてのコンテンツを非表示にし、対象のコンテンツのみ表示
        archiveContents.forEach((content) => {
          if (content.getAttribute("data-category") === targetCategory) {
            content.classList.remove("hidden");
          } else {
            content.classList.add("hidden");
          }
        });

        // タグも切り替え
        archiveTags.forEach((tag) => {
          if (tag.getAttribute("data-category") === targetCategory) {
            tag.classList.remove("hidden");
          } else {
            tag.classList.add("hidden");
          }
        });
      });
    });
  }

  // ====================
  // Archive: Load More（汎用）
  // ====================
  const loadMorePostsButtons = document.querySelectorAll(".load-more-posts");

  loadMorePostsButtons.forEach((button) => {
    button.addEventListener("click", function () {
      const currentPage = parseInt(this.getAttribute("data-page"));
      const maxPages = parseInt(this.getAttribute("data-max-pages"));
      const category = this.getAttribute("data-category");
      const action = this.getAttribute("data-action") || "load_more_posts";
      const nextPage = currentPage + 1;

      // コンテナを取得
      const container = this.closest(".archive-content");
      const articleGrid = container.querySelector(".archive-grid");

      // ボタンを無効化
      this.disabled = true;
      this.style.opacity = "0.5";

      // AJAX リクエスト
      fetch(window.location.origin + "/wp/wp-admin/admin-ajax.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          action: action,
          page: nextPage,
          category: category,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // 新しい記事を追加
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = data.data.html;

            while (tempDiv.firstChild) {
              articleGrid.appendChild(tempDiv.firstChild);
            }

            // ページ番号を更新
            this.setAttribute("data-page", nextPage);
            this.disabled = false;
            this.style.opacity = "1";

            // 最後のページに到達したらボタンを非表示
            if (nextPage >= maxPages) {
              this.style.display = "none";
            }
          } else {
            console.error("Load more failed:", data.data.message);
            this.disabled = false;
            this.style.opacity = "1";
          }
        })
        .catch((error) => {
          console.error("AJAX error:", error);
          this.disabled = false;
          this.style.opacity = "1";
        });
    });
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
